// SPDX-License-Identifier: AGPL-3.0-or-later

import {conduit} from '@app/features/platform/transport/RestTransport';
import {ConduitFailure} from '@app/features/platform/types/EndpointError';
import {replyMessage, replyRetryAfter} from '@app/features/platform/utils/ResponseInspection';

export interface VoiceDiagnosticsUploadResponse {
	id: string;
	key: string;
	size_bytes: number;
	created_at: string;
}

export class VoiceDiagnosticsUploadError extends Error {
	readonly status: number;
	readonly retryAfterSeconds?: number;

	constructor(message: string, status: number, retryAfterSeconds?: number) {
		super(message);
		this.name = 'VoiceDiagnosticsUploadError';
		this.status = status;
		this.retryAfterSeconds = retryAfterSeconds;
	}
}

export async function uploadVoiceDiagnosticsArchive(
	archive: Blob,
	filename: string,
): Promise<VoiceDiagnosticsUploadResponse> {
	try {
		const response = await conduit.post<VoiceDiagnosticsUploadResponse>('/voice-diagnostics/upload', {
			multipart: {
				files: [{name: 'archive', file: archive, filename}],
				fields: {},
			},
		});
		return response.body;
	} catch (error) {
		if (error instanceof ConduitFailure) {
			const status = error.status ?? 0;
			const message = replyMessage(error.body);
			const retryAfter = replyRetryAfter(error.body);

			if (status === 401) {
				throw new VoiceDiagnosticsUploadError(message ?? 'You must be signed in to upload voice diagnostics.', 401);
			}
			if (status === 413) {
				throw new VoiceDiagnosticsUploadError(message ?? 'Voice diagnostics archive is too large to upload.', 413);
			}
			if (status === 429) {
				throw new VoiceDiagnosticsUploadError(
					message ?? 'Voice diagnostics uploads are rate-limited. Try again later.',
					429,
					retryAfter,
				);
			}

			throw new VoiceDiagnosticsUploadError(
				message ?? `Voice diagnostics upload failed (HTTP ${status}).`,
				status,
				retryAfter,
			);
		}

		if (error instanceof Error && error.message.toLowerCase().includes('network')) {
			throw new VoiceDiagnosticsUploadError('Network error while uploading voice diagnostics.', 0);
		}

		throw new VoiceDiagnosticsUploadError(
			error instanceof Error ? error.message : 'Unknown error uploading voice diagnostics.',
			0,
		);
	}
}
